import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SeoHead from "@/components/feature/SeoHead";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { categories } from "@/mocks/categories";

export default function CategoriesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "all";
  const [activeTab, setActiveTab] = useState(initialTab);

  useEffect(() => {
    setSearchParams({ tab: activeTab });
  }, [activeTab, setSearchParams]);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [activeTab]);

  const currentCategory = categories.find((c) => c.id === activeTab);

  const allArticles: Array<{slug: string; title: string; excerpt: string; date: string; imageUrl: string; category: string}> = [];
  categories.forEach((cat) => {
    if (cat.articles) {
      cat.articles.forEach((a) => {
        allArticles.push({ slug: a.slug, title: a.title, excerpt: a.excerpt, date: a.date, imageUrl: a.imageUrl, category: cat.name });
      });
    }
  });

  const displayArticles = activeTab === "all"
    ? allArticles
    : (currentCategory?.articles?.map(a => ({ ...a, category: currentCategory.name })) || []);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "禁漫天堂内容分类导览",
    description: "浏览禁漫天堂全部内容分类，包括热血动作、恋爱言情、奇幻冒险、搞笑日常、悬疑推理、科幻未来等各类型漫画分类导览。",
    url: "https://jmtt.example.com/categories",
    inLanguage: "zh-CN",
  };

  return (
    <>
      <SeoHead
        title="内容分类导览 – 禁漫天堂 | 热血动作、恋爱言情、奇幻冒险等漫画分类"
        description="浏览禁漫天堂全部内容分类，包括热血动作、恋爱言情、奇幻冒险、搞笑日常、悬疑推理、科幻未来等各类型漫画内容分类导览与教程文章。"
        keywords="禁漫天堂内容分类,漫画分类,热血动作漫画,恋爱言情漫画,奇幻冒险漫画,搞笑日常漫画,悬疑推理漫画"
        canonicalPath="/categories"
        jsonLd={jsonLd}
      />

      <div className="pt-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-10">
          <Breadcrumb
            items={[
              { label: "首页", path: "/" },
              { label: "内容分类" },
            ]}
          />

          <div className="mt-6 mb-8">
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-3">
              内容分类导览
            </h1>
            <p className="text-foreground-500 text-sm md:text-base max-w-2xl">
              禁漫天堂拥有丰富的漫画内容分类，涵盖热血动作、恋爱言情、奇幻冒险、搞笑日常、悬疑推理、科幻未来等多种类型。点击下方标签切换浏览各分类的详细介绍与推荐内容。
            </p>
          </div>

          <div className="flex items-center gap-2 flex-wrap mb-10">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveTab(cat.id)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeTab === cat.id
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-800 border border-background-200/70"
                }`}
              >
                <i className={`${cat.icon} text-sm`}></i>
                {cat.name}
              </button>
            ))}
          </div>

          {currentCategory && activeTab !== "all" && (
            <div className="mb-10 p-6 bg-accent-100/50 border border-accent-200/60 rounded-xl">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-accent-500 text-background-50 shrink-0">
                  <i className={`${currentCategory.icon} text-2xl`}></i>
                </div>
                <div>
                  <h2 className="text-xl font-heading font-bold text-foreground-900 mb-2">
                    {currentCategory.name}
                  </h2>
                  <p className="text-foreground-600 text-sm leading-relaxed">
                    {currentCategory.description}
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {displayArticles.map((article) => (
              <Link
                key={article.slug}
                to={`/article/${article.slug}`}
                className="group bg-background-50 border border-background-200/70 rounded-xl overflow-hidden hover:border-accent-300/50 transition-all duration-300 cursor-pointer"
              >
                <div className="relative w-full h-44 overflow-hidden">
                  <img
                    src={article.imageUrl}
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-2 py-0.5 bg-accent-100 text-accent-700 rounded-md text-xs font-medium">
                      {article.category}
                    </span>
                  </div>
                  <h3 className="text-sm font-heading font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                    {article.title}
                  </h3>
                  <p className="text-foreground-500 text-xs leading-relaxed line-clamp-2 mb-3">
                    {article.excerpt}
                  </p>
                  <div className="flex items-center text-xs text-foreground-400">
                    <i className="ri-calendar-line mr-1"></i>
                    {article.date}
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {displayArticles.length === 0 && (
            <div className="text-center py-16">
              <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
                <i className="ri-inbox-line text-2xl"></i>
              </div>
              <p className="text-foreground-500 text-sm">该分类下暂无文章，敬请期待更多内容更新。</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}